import { useEffect, useState } from "react";
import { api, type SignupRequest, type SignupStatus } from "../lib/api";
import { RESERVED_FREE_PLAN } from "../lib/plans";

const FILTERS: { value: SignupStatus | "all"; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

export function Signups() {
  const [filter, setFilter] = useState<SignupStatus | "all">("pending");
  const [requests, setRequests] = useState<SignupRequest[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Per-row notes draft. Persisted in component state only. Cleared
  // ONLY on a successful review (per-row), not on refresh / filter
  // change — flipping to the "Approved" tab to glance at history
  // shouldn't lose a draft the admin is writing for a pending row.
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const refresh = (f: SignupStatus | "all" = filter) =>
    api
      .listSignups(f)
      .then((r) => setRequests(r.requests))
      .catch((e) => setErr(String((e as Error).message)));

  useEffect(() => {
    refresh(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function review(
    id: string,
    decision: "approve" | "reject",
  ): Promise<void> {
    if (
      decision === "reject" &&
      !confirm("Reject this signup? They will see a declined message.")
    ) {
      return;
    }
    const notes = notesDraft[id]?.trim() || undefined;
    setBusy(id);
    setErr(null);
    try {
      if (decision === "approve") await api.approveSignup(id, notes);
      else await api.rejectSignup(id, notes);
      // Only drop THIS row's draft on success — other in-flight drafts
      // are preserved.
      setNotesDraft((cur) => {
        const next = { ...cur };
        delete next[id];
        return next;
      });
      await refresh();
    } catch (ex) {
      setErr(String((ex as Error).message));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="main">
      <h1>Signup queue</h1>
      <p className="muted">
        Private preview — review pending sign-ups and approve the ones you
        want to let in. Approval is recorded immediately; the user's portal
        account is provisioned the next time they sign in.
      </p>

      <div className="card">
        <div style={{ display: "flex", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={`btn ${filter === f.value ? "" : "secondary"}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {err && <p style={{ color: "var(--color-danger)" }}>{err}</p>}

        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Requested</th>
              <th>Status</th>
              <th>Reviewed</th>
              <th>Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.workos_user_id}>
                <td>{r.email}</td>
                <td>
                  <code>{r.requested_at}</code>
                </td>
                <td>
                  <span className="badge">{r.status}</span>
                </td>
                <td>
                  {r.reviewed_at ? (
                    <code>{r.reviewed_at}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.status === "pending" ? (
                    <input
                      className="input"
                      placeholder="Optional reviewer note"
                      value={notesDraft[r.workos_user_id] ?? ""}
                      disabled={busy === r.workos_user_id}
                      onChange={(e) =>
                        setNotesDraft((cur) => ({
                          ...cur,
                          [r.workos_user_id]: e.target.value,
                        }))
                      }
                    />
                  ) : r.notes ? (
                    <span>{r.notes}</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {r.status === "pending" && (
                    <div style={{ display: "flex", gap: "var(--space-2)" }}>
                      <button
                        className="btn"
                        disabled={busy === r.workos_user_id}
                        onClick={() => review(r.workos_user_id, "approve")}
                      >
                        Approve
                      </button>
                      <button
                        className="btn danger"
                        disabled={busy === r.workos_user_id}
                        onClick={() => review(r.workos_user_id, "reject")}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No requests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CompGrantCard />
    </div>
  );
}

// Comp / courtesy plan grants (platform admin — same gate as this page's
// route). Fronts POST /v1/admin/accounts/-/plan: grants any catalog tier
// WITHOUT payment (billing_provider='comp', Lemon-Squeezy-immune,
// revenue-excluded), or revokes with the reserved 'locked' name. The
// target signs up self-serve first ("Skip for now" at the plan picker) —
// the grant then attaches to their existing account by member email.
function CompGrantCard() {
  const [tiers, setTiers] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Tier names come from the live catalog so a repriced/renamed tier
    // flows through. Dedupe to one entry per plan name (month + year
    // variants share the entitlement shape; the worker's comp path pins
    // the monthly row anyway).
    api
      .billing()
      .then((s) => {
        const names = [
          ...new Set(
            s.plans.map((p) => p.plan).filter((n) => n !== RESERVED_FREE_PLAN),
          ),
        ];
        setTiers(names);
        setPlan((cur) => cur || names[0] || "");
      })
      .catch(() => {
        /* catalog unavailable — the form still works with a typed name */
      });
  }, []);

  async function grant() {
    const target = email.trim();
    if (!target || !plan) return;
    const revoking = plan === "locked";
    if (
      revoking &&
      !confirm(`Revoke ${target}'s plan? Their account returns to locked.`)
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      await api.setAccountPlan(target, plan, reason.trim() || undefined);
      setResult(
        revoking
          ? `Revoked — ${target} is back to locked.`
          : `Granted ${plan} to ${target} (comp, no billing).`,
      );
      setEmail("");
      setReason("");
    } catch (ex) {
      const msg = String((ex as Error).message);
      setErr(
        msg === "not_found"
          ? "No account with that member email — they need to sign up first (they can skip the plan picker)."
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Grant plan (comp)</h2>
      <p className="muted">
        Give an account a tier without payment — test accounts, press,
        partners. The person signs up first; the grant attaches by their
        sign-in email. Pick <code>locked</code> to revoke.
      </p>

      {err && <p style={{ color: "var(--color-danger)" }}>{err}</p>}
      {result && <p className="muted">{result}</p>}

      <div
        style={{
          display: "flex",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          className="input"
          type="email"
          placeholder="member@example.com"
          value={email}
          disabled={busy}
          style={{ minWidth: 240 }}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select
          className="input"
          value={plan}
          disabled={busy}
          onChange={(e) => setPlan(e.target.value)}
        >
          {tiers.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
          <option value="locked">locked (revoke)</option>
        </select>
        <input
          className="input"
          placeholder="Reason (audit log)"
          value={reason}
          disabled={busy}
          style={{ minWidth: 200 }}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          className="btn"
          disabled={busy || !email.trim() || !plan}
          onClick={grant}
        >
          {busy ? "Granting…" : plan === "locked" ? "Revoke" : "Grant"}
        </button>
      </div>
    </div>
  );
}
